import * as React from 'react'
import { AccountDto } from '@halo/dtos/account.dto'
import { Button, Drawer, executeCleanDrawerRender, Well } from '@pushly/aqe/lib/components'
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Input, notification, Select, Skeleton, Switch } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import moment from 'moment-timezone'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { AccountService } from '@halo/services/account.service'
import { Container } from 'typescript-ioc/es5'
import { legacyValidateFields } from '@halo/_utils/antd'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import * as randomstring from 'randomstring'
import { getSupportedTimezones } from '@halo/_utils/moment'

interface IAccountDetailsEditor extends FormComponentProps {
    account: AccountDto
    visible: boolean
    onClose: (e: any) => void
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

interface IADEState {
    urlEnabled: boolean
}

export const AccountDetailsEditor = Form.create<IAccountDetailsEditor>()(
    class extends React.Component<IAccountDetailsEditor, IADEState> {
        public state: IADEState = {
            urlEnabled: !!this.account.default_notification_landing_url,
        }

        protected appState: AppState
        protected appService: AppService
        protected accountService: AccountService

        public constructor(props: IAccountDetailsEditor) {
            super(props)

            this.appState = Container.get(AppState)
            this.appService = Container.get(AppService)
            this.accountService = Container.get(AccountService)

            executeCleanDrawerRender(this)
        }

        public render() {
            const currentValues = this.props.form.getFieldsValue()

            const defaultTz = this.account.timezone ?? moment.tz.guess()
            const tzOptions = getSupportedTimezones()

            const defaultLandingUrl = this.account.default_notification_landing_url ?? 'https://{{domain_name}}/'

            return (
                <Drawer
                    className={classnames('adf-details-editor')}
                    title="Update Details"
                    placement="right"
                    closable={true}
                    onSubmit={this.handleSubmit}
                    disableSubmit={Object.keys(currentValues).length === 0}
                    onClose={this.props.onClose}
                    visible={this.props.visible}
                >
                    <Form {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}>
                        <Well title="Organization Information" mode="ghost" hideFooter={true}>
                            <Form.Item label="Org Name" className={classnames('adf-details-editor-name')}>
                                {this.props.form.getFieldDecorator('name', {
                                    initialValue: this.account.name,
                                    rules: [
                                        {
                                            required: true,
                                            message: 'Organization name is required.',
                                        },
                                    ],
                                })(<Input size="small" />)}
                            </Form.Item>

                            <Form.Item label="Default Time Zone" className={classnames('adf-details-editor')}>
                                {this.props.form.getFieldDecorator('timezone', {
                                    initialValue: defaultTz,
                                })(
                                    <Select
                                        size="small"
                                        className=""
                                        showSearch={true}
                                        placeholder="Select a time zone"
                                    >
                                        {tzOptions.map((tz) => (
                                            <Select.Option key={tz} value={tz}>
                                                {tz}
                                            </Select.Option>
                                        ))}
                                    </Select>,
                                )}
                            </Form.Item>

                            <Form.Item label="Default Landing URL" className={classnames('adf-details-editor')}>
                                <div className="switch-row">
                                    <span className="switch-left">
                                        <Switch
                                            size="small"
                                            defaultChecked={this.state.urlEnabled}
                                            onChange={async () => {
                                                await this.setState({ urlEnabled: !this.state.urlEnabled })
                                            }}
                                        />
                                    </span>
                                    {this.state.urlEnabled && (
                                        <span className="switch-right">
                                            {this.props.form.getFieldDecorator('default_notification_landing_url', {
                                                initialValue: defaultLandingUrl,
                                            })(<Input size="small" />)}
                                        </span>
                                    )}
                                </div>
                                <div className="sw-halo-form-layout-row-sub">
                                    <InfoCircleOutlined />
                                    <span>
                                        Setting a default landing URL will automatically fill this value in the “Landing
                                        URL” field during organization notification creation.
                                    </span>
                                </div>
                            </Form.Item>
                        </Well>
                    </Form>
                </Drawer>
            )
        }

        protected get account(): AccountDto {
            return this.props.account
        }

        protected getCurrentValues(): Partial<AccountDto> {
            let values: Partial<AccountDto> = {}

            values.name = this.props.form.getFieldValue('name')?.trim()
            if (values.name === this.account.name) {
                delete values.name
            }

            values.default_notification_landing_url = this.props.form
                .getFieldValue('default_notification_landing_url')
                ?.trim()
            if (!this.state.urlEnabled) {
                values.default_notification_landing_url = null
            }
            if (values.default_notification_landing_url === this.account.default_notification_landing_url) {
                delete values.default_notification_landing_url
            }

            values.timezone = this.props.form.getFieldValue('timezone')
            if (values.timezone === this.account.timezone) {
                delete values.timezone
            }

            return values
        }

        protected handleSubmit = async () => {
            try {
                await legacyValidateFields(this.props.form)
                const mergedValues = this.getCurrentValues()

                let { ok, data } = await this.accountService.patch(this.account.id, mergedValues, true)

                if (ok) {
                    this.props.onAccountUpdated(data)
                    this.props.onClose(null)
                }
            } catch (err) {
                simpleFormErrorNotification(err)
            }
        }
    },
)

interface IAccountDetailsWell {
    loading?: boolean
    account: AccountDto
    title?: string
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

interface IDWState {
    showDrawer: boolean
    drawerKey?: string
}

export class AccountDetailsWell extends React.Component<IAccountDetailsWell, IDWState> {
    public state: IDWState = {
        showDrawer: false,
    }

    protected appState: AppState

    constructor(props: IAccountDetailsWell) {
        super(props)

        this.appState = Container.get(AppState)
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Details"
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderInformationWell()}

                            <AccountDetailsEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                account={this.props.account}
                                onClose={this.handleDrawerClose}
                                onAccountUpdated={this.props.onAccountUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderInformationWell() {
        return (
            <Well title="Organization Information" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Org Name</div>
                    <div className={classnames('form-layout-row-value')}>{this.props.account.name}</div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Default Timezone</div>
                    <div className={classnames('form-layout-row-value')}>
                        {this.props.account.timezone || '(Not Set)'}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Default Landing URL</div>
                    <div className={classnames('form-layout-row-value')}>
                        {this.props.account.default_notification_landing_url || '(Not Set)'}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Created</div>
                    <div className={classnames('form-layout-row-value')}>
                        {moment(this.props.account.created_at).format('LLL')}
                        <span className="lowlight"> by </span>
                        {this.props.account.created_by_user_name}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Last Updated</div>
                    <div className={classnames('form-layout-row-value')}>
                        {moment(this.props.account.updated_at).format('LLL')}
                        <span className="lowlight"> by </span>
                        {this.props.account.updated_by_user_name}
                    </div>
                </div>
            </Well>
        )
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
